import i18next from "i18next";
import React from "react";
import "./cards.scss";

const RING_RADIUS = 58;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;

const formatCount = (value) =>
  value == null ? "—" : Number(value).toLocaleString();

function DonutChart({ segments, total, centerValue, centerLabel, label }) {
  const hasData = total > 0;
  let offset = 0;

  return (
    <div
      className="tenant-stats-donut"
      role="img"
      aria-label={label}
      title={label}
    >
      <svg viewBox="0 0 144 144" aria-hidden="true">
        <circle
          className="tenant-stats-donut-track"
          cx="72"
          cy="72"
          r={RING_RADIUS}
        />
        {hasData &&
          segments.map((segment) => {
            const fraction = Math.max(0, segment.value) / total;
            const dashLength = fraction * RING_CIRCUMFERENCE;
            const segmentOffset = offset;
            offset += dashLength;
            if (dashLength <= 0) return null;
            return (
              <circle
                className={`tenant-stats-donut-segment ${segment.className}`}
                key={segment.key}
                cx="72"
                cy="72"
                r={RING_RADIUS}
                strokeDasharray={`${dashLength} ${RING_CIRCUMFERENCE - dashLength}`}
                strokeDashoffset={-segmentOffset}
              >
                <title>
                  {i18next.t(`labels.${segment.label}`)}: {formatCount(segment.value)}
                </title>
              </circle>
            );
          })}
      </svg>
      <div className="tenant-stats-donut-center">
        <strong>{formatCount(centerValue)}</strong>
        <span>{centerLabel}</span>
      </div>
    </div>
  );
}

function Legend({ items }) {
  return (
    <div className="tenant-stats-legend">
      {items.map((item) => (
        <div className="tenant-stats-legend-item" key={item.key}>
          <span className={`tenant-stats-dot ${item.className}`} />
          <span>{i18next.t(`labels.${item.label}`)}</span>
          <strong>{formatCount(item.value)}</strong>
        </div>
      ))}
    </div>
  );
}

export default function TenantMemberStats({ stats }) {
  const activeMembers = Number(stats?.activeMembers || 0);
  const deactivatedMembers = Number(stats?.deactivatedMembers || 0);
  const totalMembers = Number(stats?.totalMembers || 0);
  const maxSeats =
    stats?.maxSeats == null ? null : Math.max(0, Number(stats.maxSeats));
  const remainingSeats =
    stats?.remainingSeats == null
      ? null
      : Math.max(0, Number(stats.remainingSeats));
  const usedSeats = maxSeats == null ? activeMembers : maxSeats - remainingSeats;
  const memberStatusTotal = activeMembers + deactivatedMembers;

  return (
    <section className="tenant-member-stats" aria-label={i18next.t("labels.member_overview")}>
      <article className="tenant-stats-card">
        <div className="tenant-stats-card-header">
          <h2>{i18next.t("labels.seat_capacity")}</h2>
          <span>{i18next.t("labels.seat_capacity_description")}</span>
        </div>
        <div className="tenant-stats-card-content">
          <DonutChart
            label={i18next.t("labels.seat_capacity_chart", {
              used: formatCount(usedSeats),
              remaining: formatCount(remainingSeats),
              max: formatCount(maxSeats),
            })}
            total={maxSeats || 0}
            centerValue={remainingSeats}
            centerLabel={i18next.t("labels.seats_available")}
            segments={[
              {
                key: "used",
                value: usedSeats,
                label: "allocated_seats",
                className: "used",
              },
              {
                key: "remaining",
                value: remainingSeats || 0,
                label: "remaining_member_seats",
                className: "remaining",
              },
            ]}
          />
          <div className="tenant-stats-card-details">
            <Legend
              items={[
                {
                  key: "allocated",
                  label: "allocated_seats",
                  value: usedSeats,
                  className: "used",
                },
                {
                  key: "remaining",
                  label: "remaining_member_seats",
                  value: remainingSeats,
                  className: "remaining",
                },
                {
                  key: "maximum",
                  label: "maximum_member_seats",
                  value: maxSeats,
                  className: "total",
                },
              ]}
            />
          </div>
        </div>
      </article>

      <article className="tenant-stats-card">
        <div className="tenant-stats-card-header">
          <h2>{i18next.t("labels.member_status")}</h2>
          <span>{i18next.t("labels.member_status_description")}</span>
        </div>
        <div className="tenant-stats-card-content">
          <DonutChart
            label={i18next.t("labels.member_status_chart", {
              active: formatCount(activeMembers),
              deactivated: formatCount(deactivatedMembers),
            })}
            total={memberStatusTotal}
            centerValue={activeMembers}
            centerLabel={i18next.t("labels.active_members")}
            segments={[
              {
                key: "active",
                value: activeMembers,
                label: "active_members",
                className: "active",
              },
              {
                key: "deactivated",
                value: deactivatedMembers,
                label: "deactivated_members",
                className: "deactivated",
              },
            ]}
          />
          <div className="tenant-stats-card-details">
            <Legend
              items={[
                {
                  key: "active",
                  label: "active_members",
                  value: activeMembers,
                  className: "active",
                },
                {
                  key: "deactivated",
                  label: "deactivated_members",
                  value: deactivatedMembers,
                  className: "deactivated",
                },
                {
                  key: "total",
                  label: "total_members",
                  value: totalMembers,
                  className: "total",
                },
              ]}
            />
          </div>
        </div>
      </article>
    </section>
  );
}
